import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	concat,
	concatMap,
	delay,
	from,
	map,
	Observable,
	of,
	Subject,
	Subscription,
	switchMap,
	tap
} from 'rxjs';

import { LiveDeviceDiscoveryApiService } from '@shure/shared/angular/data-access/device-discovery';
import { SubscriptionManagerService } from '@shure/shared/angular/data-access/system-api/core';
import { RequestResponse, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AutoRouteResult,
	CharacterizationMeasurementResult,
	CharacterizationReportResult,
	RoomAdaptDevice,
	RoomAdaptStatus,
	RoomAdaptStepEvent,
	RoomCharacterizationSummary,
	SignalContinuityResult
} from '../api/models';
import {
	AbortRoomAdaptMutationGQL,
	GetRoomAdaptDeviceQueryGQL,
	GetRoomAdaptRoomDevicesQueryGQL,
	RoomAdaptDeviceSubscriptionGQL,
	StartRoomAdaptMutationGQL,
	RoomAdaptRoomDevicesSubscriptionGQL
} from '../api-instance-sys-api/graphql/generated/room-adapt.generated';
import { SysApiRoomAdaptApiService } from '../api-instance-sys-api/sys-api-room-adapt-api.service';

import { MOCK_REPORT_GZ_BASE64 } from './arcc-report-mock-data';

/**
 * Mock extension of SysApiRoomAdaptApiService that uses real device data
 * but simulates the room adapt status progression and ARCC report results.
 * Replace with SysApiRoomAdaptApiService when the device is ready.
 */
@Injectable()
export class MockRoomAdaptApiService extends SysApiRoomAdaptApiService {
	// Auto route associating/optimizing & prompting statuses are only relevant
	// when Room Adapt is triggered via the hardware button
	private static readonly MOCK_STATUS_SEQUENCE: RoomAdaptStatus[] = [
		RoomAdaptStatus.AutoRouteAssociating,
		RoomAdaptStatus.AutoRouteOptimizing,
		RoomAdaptStatus.RoomCharacterizationPrompting,
		RoomAdaptStatus.ContinuityCheck,
		RoomAdaptStatus.RoomCharacterizationInProgress,
		RoomAdaptStatus.GeneratingReport
	];

	private static readonly MOCK_STEP_DURATION_MS = 3_000;

	/**
	 * Gap between status transitioning to Idle and the final summary update.
	 * Expected behaviour: DeviceRoomAdapt and DeviceRoomCharacterization arrive
	 * as separate node-change events, so the status change leads the summary.
	 */
	private static readonly MOCK_SUMMARY_LAG_MS = 250;

	private readonly mockStatusOverrides = new Map<string, BehaviorSubject<RoomAdaptStatus | null>>();
	private readonly stepEventSubjects = new Map<string, Subject<RoomAdaptStepEvent>>();
	private readonly summarySubjects = new Map<string, BehaviorSubject<RoomCharacterizationSummary | null>>();
	private readonly simulationSubscriptions = new Map<string, Subscription>();
	private readonly mockLogger: ILogger;

	constructor(
		logger: ILogger,
		getDeviceQueryGQL: GetRoomAdaptDeviceQueryGQL,
		startRoomAdaptMutationGQL: StartRoomAdaptMutationGQL,
		abortRoomAdaptMutationGQL: AbortRoomAdaptMutationGQL,
		deviceSubscriptionGQL: RoomAdaptDeviceSubscriptionGQL,
		getRoomDevicesQueryGQL: GetRoomAdaptRoomDevicesQueryGQL,
		deviceDiscoveryApiService: LiveDeviceDiscoveryApiService,
		roomDevicesSubscriptionGQL: RoomAdaptRoomDevicesSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		super(
			logger,
			getDeviceQueryGQL,
			startRoomAdaptMutationGQL,
			abortRoomAdaptMutationGQL,
			getRoomDevicesQueryGQL,
			deviceDiscoveryApiService,
			deviceSubscriptionGQL,
			roomDevicesSubscriptionGQL,
			subscriptionManagerService
		);
		this.mockLogger = logger.createScopedLogger('MockRoomAdaptApiService');
	}

	/** Overlays mock room adapt status, summary, and report availability onto real device data. */
	public override getDevice$(deviceId: string): Observable<RoomAdaptDevice> {
		// Drive emissions off both the status override and the summary override so
		// summary updates can land in their own node-change event (matching real sys-api).
		return combineLatest([this.getStatusOverride$(deviceId), this.getSummaryOverride$(deviceId)]).pipe(
			switchMap(([mockStatus, summary]) =>
				super.getDevice$(deviceId).pipe(
					map((device) => {
						const rc = device.features.roomCharacterization;
						const ra = device.features.roomAdapt;

						return {
							...device,
							features: {
								...device.features,
								roomCharacterization: rc
									? {
											isMissing: false,
											readiness: mockStatus === null,
											reportDownloadAvailable:
												summary?.characterizationReportResult ===
												CharacterizationReportResult.Successful,
											summary
										}
									: undefined,
								roomAdapt: ra
									? {
											isMissing: false,
											status: mockStatus ?? RoomAdaptStatus.Idle
										}
									: undefined
							}
						};
					})
				)
			)
		);
	}

	/**
	 * Resets the device summary to initial state and starts a new mock status simulation.
	 * On subsequent calls, previous summary is overwritten and the status override is reset to Idle.
	 */
	public override startRoomAdapt(deviceId: string): Observable<RequestResponse<string>> {
		this.getOrCreateSummaryOverride(deviceId).next(this.createInitialSummary());
		this.getOrCreateStatusOverride(deviceId).next(RoomAdaptStatus.Idle);
		this.startMockStatusSimulation(deviceId);
		return of({ state: ResponseState.Done });
	}

	/** Sets the status override to null, signaling that room adapt is no longer active. */
	public override abortRoomAdapt(deviceId: string): void {
		this.simulationSubscriptions.get(deviceId)?.unsubscribe();
		this.simulationSubscriptions.delete(deviceId);

		const summarySubject = this.getOrCreateSummaryOverride(deviceId);
		const current = summarySubject.getValue();
		if (current) {
			summarySubject.next({ ...current, characterizationReportResult: CharacterizationReportResult.Aborted });
		}

		this.getOrCreateStatusOverride(deviceId).next(null);
	}

	/** Returns mock ARCC report data if the simulation completed successfully, otherwise null. */
	public override retrieveRoomCharacterizationReport(deviceId: string): Observable<string | null> {
		if (
			this.getOrCreateSummaryOverride(deviceId).getValue()?.characterizationReportResult ===
			CharacterizationReportResult.Successful
		) {
			return of(MOCK_REPORT_GZ_BASE64);
		}
		return of(null);
	}

	private getStatusOverride$(deviceId: string): Observable<RoomAdaptStatus | null> {
		return this.getOrCreateStatusOverride(deviceId).asObservable();
	}

	private getSummaryOverride$(deviceId: string): Observable<RoomCharacterizationSummary | null> {
		return this.getOrCreateSummaryOverride(deviceId).asObservable();
	}

	private getOrCreateStatusOverride(deviceId: string): BehaviorSubject<RoomAdaptStatus | null> {
		let subject = this.mockStatusOverrides.get(deviceId);
		if (!subject) {
			subject = new BehaviorSubject<RoomAdaptStatus | null>(null);
			this.mockStatusOverrides.set(deviceId, subject);
		}
		return subject;
	}

	private getOrCreateSummaryOverride(deviceId: string): BehaviorSubject<RoomCharacterizationSummary | null> {
		let subject = this.summarySubjects.get(deviceId);
		if (!subject) {
			subject = new BehaviorSubject<RoomCharacterizationSummary | null>(null);
			this.summarySubjects.set(deviceId, subject);
		}
		return subject;
	}

	private getOrCreateStepEventSubject(deviceId: string): Subject<RoomAdaptStepEvent> {
		let subject = this.stepEventSubjects.get(deviceId);
		if (!subject) {
			subject = new Subject<RoomAdaptStepEvent>();
			this.stepEventSubjects.set(deviceId, subject);
		}
		return subject;
	}

	private startMockStatusSimulation(deviceId: string): void {
		const steps = MockRoomAdaptApiService.MOCK_STATUS_SEQUENCE;
		const stepDuration = MockRoomAdaptApiService.MOCK_STEP_DURATION_MS;
		const summaryLag = MockRoomAdaptApiService.MOCK_SUMMARY_LAG_MS;
		const statusSubject = this.getOrCreateStatusOverride(deviceId);

		const finalStep = steps.at(-1);

		// Per step: emit in-progress status, wait, then emit that step's result.
		// For the final step (GeneratingReport), defer the result until after
		// status flips to Idle to approximate expected node-change event ordering.
		const inProgressSteps$ = from(steps).pipe(
			concatMap((status) =>
				of(status).pipe(
					tap((s) => {
						this.mockLogger.trace('startMockStatusSimulation()', `Mock status: ${s}`, {
							deviceId
						});
						statusSubject.next(s);
					}),
					delay(stepDuration),
					tap((s) => {
						if (s !== finalStep) {
							this.emitStepResult(deviceId, s);
						}
					})
				)
			)
		);

		const finalizeRun$ = of(null).pipe(
			tap(() => statusSubject.next(null)),
			delay(summaryLag),
			tap(() => {
				if (finalStep) {
					this.emitStepResult(deviceId, finalStep);
				}
			})
		);

		const subscription = concat(inProgressSteps$, finalizeRun$).subscribe({
			complete: () => {
				this.mockLogger.trace('startMockStatusSimulation()', 'Mock simulation complete', { deviceId });
				this.simulationSubscriptions.delete(deviceId);
			}
		});
		this.simulationSubscriptions.set(deviceId, subscription);
	}

	private emitStepResult(deviceId: string, status: RoomAdaptStatus): void {
		const summarySubject = this.getOrCreateSummaryOverride(deviceId);
		const current = summarySubject.getValue();
		const stepEvents = this.getOrCreateStepEventSubject(deviceId);
		if (!current) {
			return;
		}

		switch (status) {
			case RoomAdaptStatus.ContinuityCheck:
				summarySubject.next({ ...current, signalContinuityResult: SignalContinuityResult.Full });
				stepEvents.next({ type: 'signalContinuity', result: SignalContinuityResult.Full });
				break;
			case RoomAdaptStatus.RoomCharacterizationInProgress:
				summarySubject.next({
					...current,
					ambientNoise: 45,
					coupling: 72,
					distortion: -40,
					reverberation: 490,
					characterizationMeasurementResult: CharacterizationMeasurementResult.Successful
				});
				stepEvents.next({
					type: 'characterizationSummary',
					result: CharacterizationMeasurementResult.Successful
				});
				break;
			case RoomAdaptStatus.GeneratingReport:
				summarySubject.next({
					...current,
					characterizationReportResult: CharacterizationReportResult.Successful
				});
				stepEvents.next({ type: 'reportGeneration', result: CharacterizationReportResult.Successful });
				break;
			case RoomAdaptStatus.AutoRouteOptimizing:
				summarySubject.next({ ...current, autoRouteResult: AutoRouteResult.Successful });
				stepEvents.next({ type: 'autoRoute', result: AutoRouteResult.Successful });
				break;
		}
	}

	private createInitialSummary(): RoomCharacterizationSummary {
		return {
			ambientNoise: 0,
			coupling: 0,
			distortion: 0,
			reverberation: 0,
			signalContinuityResult: null,
			autoRouteResult: null,
			characterizationMeasurementResult: null,
			characterizationReportResult: null
		};
	}
}
